/**
 * Hook for persisting AI chat history to localStorage
 */

import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'hermit-chat-history';
const MAX_MESSAGES = 50; // Keep last 50 messages

import { WizardStep, NodeType } from '../types/configurator';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  actions?: Array<{
    label: string;
    step?: WizardStep;
    action?: 'analyze' | 'export' | 'addNode';
    nodeType?: NodeType;
  }>;
}

interface ChatHistoryState {
  messages: ChatMessage[];
  lastUpdated: number;
}

const WELCOME_MESSAGE: ChatMessage = {
  id: 'welcome',
  role: 'assistant',
  content: `Welcome to hermit-μDC! I'm your AI assistant. I can help you:

- Choose the right scenario for your use case
- Configure network settings
- Add and configure nodes
- Allocate services optimally
- Set up security policies

What would you like help with?`,
  timestamp: Date.now(),
};

function loadFromStorage(): ChatMessage[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed: ChatHistoryState = JSON.parse(stored);
      // Check if data is less than 24 hours old
      const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
      if (parsed.lastUpdated > oneDayAgo && parsed.messages.length > 0) {
        return parsed.messages;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return [WELCOME_MESSAGE];
}

function saveToStorage(messages: ChatMessage[]): void {
  try {
    // Keep only the last MAX_MESSAGES
    const trimmed = messages.slice(-MAX_MESSAGES);
    const state: ChatHistoryState = {
      messages: trimmed,
      lastUpdated: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage errors (quota exceeded, etc.)
  }
}

export function useChatHistory() {
  const [messages, setMessages] = useState<ChatMessage[]>(() => loadFromStorage());

  // Save to localStorage whenever messages change
  useEffect(() => {
    if (messages.length > 1) { // Don't save if only welcome message
      saveToStorage(messages);
    }
  }, [messages]);

  const addMessage = useCallback((message: ChatMessage) => {
    setMessages(prev => [...prev, message]);
  }, []);

  const updateMessage = useCallback((id: string, updates: Partial<ChatMessage>) => {
    setMessages(prev =>
      prev.map(m => (m.id === id ? { ...m, ...updates } : m))
    );
  }, []);

  const clearHistory = useCallback(() => {
    setMessages([WELCOME_MESSAGE]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return {
    messages,
    setMessages,
    addMessage,
    updateMessage,
    clearHistory,
  };
}
